import React, { useState } from "react"
import { useNavigate } from "react-router-dom"
import {Phone, Calendar} from 'lucide-react'
import toast from "react-hot-toast"
import { lumi } from "@/lib/lumi"

interface ServiceOption {
  value: string
  label: string
}

interface EmbeddedQuoteFormProps {
  /** Pre-selected service value for the dropdown */
  defaultService?: string
  /** Heading shown above the form */
  heading?: string
  /** Subheading / supporting copy */
  subheading?: string
  /** Optional custom service dropdown options */
  serviceOptions?: ServiceOption[]
  /** Corvette landing-page field layout */
  variant?: "default" | "corvette"
}

const SERVICE_OPTIONS = [
  { value: "ppf", label: "Paint Protection Film (PPF)" },
  { value: "colored-ppf", label: "Colored Paint Protection Film" },
  { value: "ceramic-coating", label: "Ceramic Coating" },
  { value: "vehicle-wrap", label: "Vehicle Wrap / Color Change" },
  { value: "window-tint", label: "Window Tinting" },
  { value: "chrome-delete", label: "Chrome Delete" },
  { value: "fleet-wraps", label: "Fleet Wraps" },
  { value: "caliper-painting", label: "Caliper Painting" },
  { value: "other", label: "Not Sure — Help Me Choose" },
]

const EmbeddedQuoteForm: React.FC<EmbeddedQuoteFormProps> = ({
  defaultService = "",
  heading = "Get a Free Quote",
  subheading = "Every project is unique. Tell us about your vehicle and we'll be in touch shortly.",
  serviceOptions,
  variant = "default",
}) => {
  const navigate = useNavigate()
  const isCorvette = variant === "corvette"
  const options = serviceOptions && serviceOptions.length > 0 ? serviceOptions : SERVICE_OPTIONS
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    vehicle: "",
    year: "",
    model: "",
    service: defaultService,
    message: "",
  })
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!formData.name || !formData.phone || !formData.email) {
      toast.error("Please fill in all required fields.")
      return
    }
    setSubmitting(true)
    try {
      const serviceLabel = options.find((opt) => opt.value === formData.service)?.label || formData.service
      const vehicleLabel = isCorvette
        ? [formData.year, "Corvette", formData.model].filter(Boolean).join(" ")
        : formData.vehicle
      const corvetteLeadNotes = isCorvette
        ? [
            formData.year ? `Year: ${formData.year}` : null,
            formData.model ? `Corvette Model: ${formData.model}` : null,
            serviceLabel ? `Service Interested In: ${serviceLabel}` : null,
          ].filter(Boolean).join(" | ")
        : ""
      const payload = {
        name: formData.name,
        phone: formData.phone,
        email: formData.email,
        vehicle: vehicleLabel,
        service: isCorvette ? serviceLabel : formData.service,
        message: isCorvette
          ? [corvetteLeadNotes, formData.message].filter(Boolean).join("\n\n")
          : formData.message,
      }
      const response = await lumi.functions.invoke("contact-submit", {
        method: "POST",
        body: payload,
      })
      if (response.success) {
        navigate("/thank-you")
        window.scrollTo({ top: 0, behavior: "smooth" })
      } else {
        toast.error("Submission failed. Please call us directly at 858-282-8119.")
      }
    } catch {
      toast.error("Submission failed. Please call us directly at 858-282-8119.")
    } finally {
      setSubmitting(false)
    }
  }

  if (submitted) {
    return (
      <div className="bg-gray-900 rounded-2xl border border-cyan-500/20 p-8 text-center">
        <div className="bg-cyan-500/20 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 border border-cyan-500">
          <svg className="text-cyan-400 w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h3 className="text-2xl font-bold text-white mb-2">Request Received!</h3>
        <p className="text-gray-300 mb-6">We'll reach out shortly to discuss your project and provide a custom quote.</p>
        <a
          href="tel:858-282-8119"
          className="inline-flex items-center gap-2 bg-cyan-500 hover:bg-cyan-600 text-white font-bold py-3 px-8 rounded-lg transition"
        >
          <Phone size={18} />
          Call Now: 858-282-8119
        </a>
      </div>
    )
  }

  return (
    <div className="bg-gray-900 rounded-2xl border border-cyan-500/20 p-8">
      <h3 className="text-2xl font-bold text-white mb-2">{heading}</h3>
      {subheading && <p className="text-gray-400 mb-6 text-sm leading-relaxed">{subheading}</p>}

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Name */}
        <div>
          <label htmlFor="eq-name" className="block text-sm font-medium text-gray-300 mb-1">
            Name <span className="text-orange-400">*</span>
          </label>
          <input
            id="eq-name"
            type="text"
            name="name"
            value={formData.name}
            onChange={handleChange}
            required
            placeholder="John Smith"
            className="w-full px-4 py-3 bg-black border border-gray-700 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent outline-none transition text-white placeholder-gray-500"
          />
        </div>

        {/* Phone */}
        <div>
          <label htmlFor="eq-phone" className="block text-sm font-medium text-gray-300 mb-1">
            Phone <span className="text-orange-400">*</span>
          </label>
          <input
            id="eq-phone"
            type="tel"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            required
            placeholder="858-282-8119"
            className="w-full px-4 py-3 bg-black border border-gray-700 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent outline-none transition text-white placeholder-gray-500"
          />
        </div>

        {/* Email */}
        <div>
          <label htmlFor="eq-email" className="block text-sm font-medium text-gray-300 mb-1">
            Email <span className="text-orange-400">*</span>
          </label>
          <input
            id="eq-email"
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            required
            placeholder="john@example.com"
            className="w-full px-4 py-3 bg-black border border-gray-700 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent outline-none transition text-white placeholder-gray-500"
          />
        </div>

        {isCorvette ? (
          <>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label htmlFor="eq-year" className="block text-sm font-medium text-gray-300 mb-1">
                  Year
                </label>
                <input
                  id="eq-year"
                  type="text"
                  name="year"
                  value={formData.year}
                  onChange={handleChange}
                  placeholder="2024"
                  className="w-full px-4 py-3 bg-black border border-gray-700 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent outline-none transition text-white placeholder-gray-500"
                />
              </div>
              <div>
                <label htmlFor="eq-model" className="block text-sm font-medium text-gray-300 mb-1">
                  Corvette Model
                </label>
                <input
                  id="eq-model"
                  type="text"
                  name="model"
                  value={formData.model}
                  onChange={handleChange}
                  placeholder="C8 Stingray, Z06, E-Ray"
                  className="w-full px-4 py-3 bg-black border border-gray-700 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent outline-none transition text-white placeholder-gray-500"
                />
              </div>
            </div>
          </>
        ) : (
          <div>
            <label htmlFor="eq-vehicle" className="block text-sm font-medium text-gray-300 mb-1">
              Vehicle (Year, Make, Model)
            </label>
            <input
              id="eq-vehicle"
              type="text"
              name="vehicle"
              value={formData.vehicle}
              onChange={handleChange}
              placeholder="2023 Tesla Model Y"
              className="w-full px-4 py-3 bg-black border border-gray-700 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent outline-none transition text-white placeholder-gray-500"
            />
          </div>
        )}

        {/* Service */}
        <div>
          <label htmlFor="eq-service" className="block text-sm font-medium text-gray-300 mb-1">
            Service Interested In
          </label>
          <select
            id="eq-service"
            name="service"
            value={formData.service}
            onChange={handleChange}
            className="w-full px-4 py-3 bg-black border border-gray-700 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent outline-none transition text-white"
          >
            <option value="">Select a service</option>
            {options.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        {/* Message */}
        <div>
          <label htmlFor="eq-message" className="block text-sm font-medium text-gray-300 mb-1">
            Additional Details
          </label>
          <textarea
            id="eq-message"
            name="message"
            value={formData.message}
            onChange={handleChange}
            rows={3}
            placeholder="Tell us about your project, any questions, or specific goals..."
            className="w-full px-4 py-3 bg-black border border-gray-700 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent outline-none transition text-white placeholder-gray-500"
          />
        </div>

        <button
          type="submit"
          disabled={submitting}
          className="w-full bg-orange-500 hover:bg-orange-600 disabled:opacity-60 text-white font-bold py-4 px-6 rounded-lg transition-all transform hover:scale-105 shadow-lg flex items-center justify-center gap-2"
        >
          <Calendar size={18} />
          {submitting ? "Sending..." : "Get My Free Quote"}
        </button>

        <p className="text-xs text-gray-500 text-center">
          We typically respond within a few hours · No spam, ever
        </p>
      </form>

      <div className="mt-5 pt-5 border-t border-gray-800 text-center">
        <p className="text-gray-400 text-sm mb-2">Prefer to talk? Call or text us directly:</p>
        <a
          href="tel:858-282-8119"
          className="inline-flex items-center gap-2 text-cyan-400 hover:text-cyan-300 font-bold text-lg transition"
        >
          <Phone size={18} />
          858-282-8119
        </a>
      </div>
    </div>
  )
}

export default EmbeddedQuoteForm
